import {
  Badge,
  Box,
  Checkbox,
  Flex,
  Form,
  Small,
  Switch,
  Text,
} from '@bigcommerce/big-design';

import type { LocationFormData } from './types';

interface PaymentsSettingsTabProps {
  formData: LocationFormData;
  onChange: (data: Partial<LocationFormData>) => void;
}

export function PaymentsSettingsTab({
  formData,
  onChange,
}: PaymentsSettingsTabProps) {
  const updatePaymentMethod = (method: keyof LocationFormData['paymentMethods']) => {
    onChange({
      paymentMethods: {
        ...formData.paymentMethods,
        [method]: !formData.paymentMethods[method],
      },
    });
  };

  const toggleTip = () => {
    onChange({
      tip: { ...formData.tip, enabled: !formData.tip.enabled },
    });
  };

  return (
    <Form>
      <Box marginBottom="medium">
        <Text bold marginBottom="xSmall">Payment Methods</Text>
        <Small color="secondary60" marginBottom="small">
          Enable or disable payment methods for this location
        </Small>
        <Flex flexDirection="column" flexGap="small">
          <Checkbox
            label="Cash"
            checked={formData.paymentMethods.cash}
            onChange={() => updatePaymentMethod('cash')}
          />
          <Checkbox
            label="Card (Stripe Terminal)"
            description="Accept card payments via Tap to Pay"
            checked={formData.paymentMethods.card}
            onChange={() => updatePaymentMethod('card')}
          />
          <Checkbox
            label="Manual Card Entry"
            description="Key in card details manually (higher fees)"
            checked={formData.paymentMethods.manualCard}
            onChange={() => updatePaymentMethod('manualCard')}
          />
        </Flex>
      </Box>

      <Box marginTop="large">
        <Flex alignItems="center" justifyContent="space-between" marginBottom="small">
          <Box>
            <Text bold>Tips</Text>
            <Small color="secondary60">Allow customers to add a tip at checkout</Small>
          </Box>
          <Switch
            checked={formData.tip.enabled}
            onChange={toggleTip}
          />
        </Flex>

        {formData.tip.enabled && (
          <Box marginTop="small">
            <Text color="secondary60" marginBottom="xSmall">Tip Options (percentages)</Text>
            <Flex flexGap="small">
              {formData.tip.options.map((option, index) => (
                <Badge key={index} variant="secondary" label={`${option}%`} />
              ))}
            </Flex>
            <Small color="secondary60" marginTop="xSmall">
              Default tip options: 10%, 15%, 20%
            </Small>
          </Box>
        )}
      </Box>
    </Form>
  );
}
